import { shortIdBuilder } from '@racemap/utilities/functions/utils';
import type { WMSLayerObject } from '@racemap/utilities/types/geos';
import { type FC, memo, useContext, useEffect, useRef } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import shortHash from 'shorthash2';
import { MapContext } from '../BasicMap/BasicMap';

export interface Props {
  properties: WMSLayerObject['properties'];
}

export const WMSRasterLayer: FC<Props> = memo(function WMSRasterLayer({ properties }: Props) {
  const { current: id } = useRef(`wms-raster-${shortIdBuilder()}`);
  const { mapStyle, map } = useContext(MapContext);

  useEffect(() => {
    if (map == null || !map.isStyleLoaded()) return;

    // be sure, that external layers are under all other custom layers. "country-label" is the
    // last mapbox map layer of the vector tiles. If the map style is a satellite style, the layer "satellite" is
    // the last layer.

    // satellite
    if (mapStyle?.sprite?.includes('satellite')) {
      map.moveLayer(`layer-${id}`, 'satellite');
      return;
    }

    // rest
    map.moveLayer(`layer-${id}`, 'country-label');
  }, [mapStyle?.sprite]);

  if (properties.tiles.length === 0 || !Number.isFinite(properties.tileSize)) return <></>;

  const hash = shortHash(properties.tiles.join('') + properties.tileSize.toString());

  return (
    <Source
      id={id}
      key={`${id}-${hash}`}
      type="raster"
      tiles={properties.tiles}
      tileSize={properties.tileSize}
    >
      <Layer id={`layer-${id}`} key={`layer-${id}`} type="raster" source={id} />
    </Source>
  );
});
